/**
 * Account resolution — maps Firebase Auth UIDs to canonical account profiles.
 *
 * Resolution chain (Phase 1):
 *  1. Check users/{authUid}/account_membership/main — multi-user firm membership
 *  2. Check accounts/{authUid}/profile/main         — direct UID = accountId (Phase 1 default)
 *  3. Return null                                    — non-migrated user
 *
 * DO NOT touch accounts/{hash}/... (canonical auth/email mapping) — that path is
 * managed exclusively by canonicalAuth.ts / emailMapping.ts and must remain untouched.
 */

import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { ACCOUNT_FIRESTORE_PREFIX } from './constants';
import { pickBackPointerAccountId } from './backPointer';
import type {
  AccountProfile,
  AccountMembership,
  AccountResolution,
  MemberRole,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Synchronous helpers (legacy / kept for existing callers)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the current Firebase Auth UID synchronously.
 * Returns null if no user is signed in.
 *
 * @deprecated Prefer resolveAccount() which returns the full AccountResolution.
 */
export function resolveAccountIdFromAuth(): string | null {
  try {
    const auth = getAuth(getApp());
    return auth.currentUser?.uid ?? null;
  } catch (err) {
    console.warn('[Prest0nRuntime] Failed to resolve account ID from auth:', err);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Core account resolution
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves a Firebase auth UID to a canonical AccountResolution.
 *
 * Returns null for non-migrated / unrecognised users.  Callers should fall
 * back to the legacy per-user orchestrator path when null is returned.
 */
export async function resolveAccount(authUid: string): Promise<AccountResolution | null> {
  try {
    return await resolveAccountStrict(authUid);
  } catch (err) {
    console.warn('[Prest0nRuntime] resolveAccount failed:', err);
    return null;
  }
}

/**
 * R30.1 (2026-08-18 interactive-cat dead-turn incident): same resolution chain,
 * but a READ ERROR is rethrown instead of being folded into the authoritative
 * "non-migrated user" null. The distinction matters because the migration gate's
 * sticky-positive shield (Round J B1) can only engage when it can tell "Firestore
 * momentarily failed" apart from "this user genuinely has no account" — the
 * catch-to-null here made both look identical, so a transient blip (or an
 * auth-hydration race in a fresh incognito session) silently rerouted a migrated
 * account onto the legacy orchestrator lane.
 */
export async function resolveAccountStrict(authUid: string): Promise<AccountResolution | null> {
  if (!authUid) return null;

  {
    const db = getFirestore(getApp());

    // ── Step 1: check for an explicit firm-level membership ───────────────────
    const membershipRef = doc(db, 'users', authUid, 'account_membership', 'main');
    const membershipSnap = await getDoc(membershipRef);

    if (membershipSnap.exists()) {
      const membership = membershipSnap.data() as AccountMembership;
      // Tolerant of both back-pointer shapes so an invited user resolves without
      // a hand-edit (see backPointer.ts / rbacAcceptInvitation).
      const accountId = pickBackPointerAccountId(membership as unknown as Record<string, unknown>);

      if (accountId) {
        const profileRef = doc(db, ACCOUNT_FIRESTORE_PREFIX, accountId, 'profile', 'main');
        const profileSnap = await getDoc(profileRef);

        if (profileSnap.exists()) {
          const profile = profileSnap.data() as AccountProfile;
          return buildResolution(accountId, authUid, profile, membership.role || 'member');
        }

        console.warn('[Prest0nRuntime] account_membership points to missing account', {
          authUid,
          accountId,
        });
        // Fall through to direct-UID check below
      }
    }

    // ── Step 2: direct UID = accountId (Phase 1 default) ─────────────────────
    const directProfileRef = doc(db, ACCOUNT_FIRESTORE_PREFIX, authUid, 'profile', 'main');
    const directProfileSnap = await getDoc(directProfileRef);

    if (directProfileSnap.exists()) {
      const profile = directProfileSnap.data() as AccountProfile;
      return buildResolution(authUid, authUid, profile, 'owner');
    }

    // ── Step 3: non-migrated user ─────────────────────────────────────────────
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Firestore profile loader (kept for legacy callers in index.ts etc.)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Loads the account profile document directly from Firestore.
 * Returns null if the document does not exist or on error.
 */
export async function loadAccountProfile(accountId: string): Promise<AccountProfile | null> {
  try {
    const db = getFirestore(getApp());
    const profileRef = doc(db, ACCOUNT_FIRESTORE_PREFIX, accountId, 'profile', 'main');
    const snapshot = await getDoc(profileRef);
    if (!snapshot.exists()) return null;
    return { accountId, ...snapshot.data() } as AccountProfile;
  } catch (err) {
    console.warn('[Prest0nRuntime] Failed to load account profile:', err);
    return null;
  }
}

/**
 * Checks whether an account profile has active status.
 */
export function isAccountActive(profile: AccountProfile): boolean {
  return profile.status === 'active';
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Strictly resolve ONE named workspace for this user — the membership check
 * behind the per-tab organization pin (tabWorkspace.ts).
 *
 * `accounts/{accountId}/profile/main` is readable only by a member
 * (firestore.rules: `allow read: if isAccountMember(accountId) || ...`), so a
 * successful read IS the membership proof — the same fact the server re-checks
 * with verifyWorkspaceAccess on every call. A denial or a missing profile
 * returns null and the caller drops to the next tier of the ladder, so a pin
 * naming an organization the user was removed from can never hold the view.
 *
 * THROWS on a transient read failure, exactly like resolveAccountStrict: a
 * blip must not read as "you are not a member" (INCIDENT-20260818 — an instant
 * negative flipped session identity and unfiltered the thread rail).
 */
export async function resolveNamedWorkspaceStrict(
  authUid: string,
  accountId: string,
): Promise<AccountResolution | null> {
  const uid = String(authUid ?? '').trim();
  const target = String(accountId ?? '').trim();
  if (!uid || !target) return null;

  const db = getFirestore(getApp());
  const profileSnap = await getDoc(doc(db, ACCOUNT_FIRESTORE_PREFIX, target, 'profile', 'main'));
  if (!profileSnap.exists()) return null;

  const profile = profileSnap.data() as AccountProfile;
  const role: MemberRole = target === uid ? 'owner' : 'member';
  return buildResolution(target, uid, profile, role);
}

function buildResolution(
  accountId: string,
  userId: string,
  profile: AccountProfile,
  role: MemberRole
): AccountResolution {
  const env = resolveEnvironment(profile);
  return {
    accountId,
    userId,
    role,
    firmName: profile.firmName || profile.displayName || 'Law Firm',
    tenantId: profile.tenantId ?? null,
    environment: env,
    isMigrated: true, // if we have a profile doc, the account is migrated
  };
}

function resolveEnvironment(profile: AccountProfile): AccountResolution['environment'] {
  switch (profile.accountType) {
    case 'testing':
      return 'testing';
    default:
      return 'production';
  }
}
